import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const ThemeSelect = ({ value, onChange, options, icon: Icon, placeholder, label, allowCustomInput, hideLabelOnSelect, className = '' }) => {
    const { currentTheme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        
        if (!isInputFocused) {
            setInputValue(value);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, isInputFocused]);

    const selectedOption = options.find(opt => typeof opt === 'object' ? opt.value === value : opt === value);
    const displayValue = typeof value === 'object' ? value.label : (selectedOption?.label || selectedOption || value || placeholder);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        onChange(val);
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {label && hideLabelOnSelect && value && value !== 'None' && value !== 'all_time' && value !== '10' && value !== '' && (
                <span className="absolute -top-5 left-1 text-[11px] font-extrabold uppercase tracking-widest" style={{ color: currentTheme.color }}>
                    {label.replace(':', '')}
                </span>
            )}
            <div
                className="flex items-center space-x-2 text-white rounded-xl px-4 py-2.5 desktop:px-5 desktop:py-3.5 transition-all hover:shadow-md w-full cursor-pointer border border-white/20"
                style={{ backgroundColor: currentTheme.color }}
                onClick={() => setIsOpen(!isOpen)}
            >
                {Icon && <Icon className="w-5 h-5 desktop:w-6 desktop:h-6 flex-shrink-0" />}
                <div className="font-bold text-[14px] desktop:text-[16px] truncate flex-1 text-left flex items-center">
                    {label && (!hideLabelOnSelect || !value || value === 'None' || value === 'all_time' || value === '10' || value === '' || (allowCustomInput && !inputValue)) && (
                        <span className="opacity-90 mr-1 whitespace-nowrap">{label} </span>
                    )}
                    {allowCustomInput ? (
                        <input
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isOpen) setIsOpen(false); // don't toggle dropdown on input click
                            }}
                            className="bg-white/30 border-none outline-none font-bold px-2 py-0.5 rounded-md w-full text-white placeholder-white/70"
                            placeholder={placeholder}
                        />
                    ) : (
                        <span className="truncate">{displayValue}</span>
                    )}
                </div>
                <ChevronDown className={`w-4 h-4 desktop:w-5 desktop:h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-[100] max-h-72 overflow-y-auto animate-in fade-in zoom-in duration-200 origin-top hide-scrollbar">
                    {options.map((option) => {
                        const optionValue = typeof option === 'object' ? option.value : option;
                        const optionLabel = typeof option === 'object' ? option.label : option;
                        const isSelected = value === optionValue;

                        return (
                            <button
                                key={optionValue}
                                onClick={() => {
                                    onChange(optionValue);
                                    setIsOpen(false);
                                }}
                                className={`w-full px-5 py-3 text-left text-sm desktop:text-lg transition-all duration-200 flex items-center justify-between ${isSelected ? 'text-white font-bold scale-[1.02]' : 'text-gray-700 hover:pl-7'
                                    }`}
                                style={{
                                    backgroundColor: isSelected ? currentTheme.color : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = `${currentTheme.color}15`;
                                        e.currentTarget.style.color = currentTheme.color;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#374151'; // text-gray-700
                                    }
                                }}
                            >
                                <span className="truncate">{optionLabel}</span>
                                {isSelected && (
                                    <div className="bg-white/20 p-1 rounded-full">
                                        <svg className="w-4 h-4 desktop:w-5 desktop:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ThemeSelect;
